import { useEffect, useState } from 'react';
import { Location } from 'history';
import { parseQueryParams } from '@/utils/queryParams';
import { useHistory } from 'react-router-dom';
import usePrevious from '@/hooks/usePrevious';

const buildUrlFromLocation = ({ hash, pathname, search }): string => {
    return `${pathname}${search || ''}${hash || ''}`;
};

/**
 * Restores scroll position when returning to previously visited routes
 * as well as setting scroll position on new pages to (0,0)
 * @see https://reacttraining.com/react-router/web/guides/scroll-restoration
 */
type ScrollMemoryProps = { currLocation: Location; routeIsPreloading: boolean };
const ScrollMemory = ({ currLocation, routeIsPreloading }: ScrollMemoryProps) => {
    const history = useHistory();
    const prevLocation = usePrevious(currLocation);
    const [historyScrollLocations, setHistoryScrollLocations] = useState(new Map());

    useEffect(() => {
        const currLocationUrl = buildUrlFromLocation(currLocation);

        if (routeIsPreloading) {
            return;
        }

        const onNewLocation = history.listen((newLocation) => {
            if (currLocationUrl !== buildUrlFromLocation(newLocation)) {
                setHistoryScrollLocations(historyScrollLocations.set(currLocation.key, window.scrollY));
            }
        });

        // Get saved scroll position for current page
        const historicalLocation = historyScrollLocations.get(currLocation.key);

        if (historicalLocation) {
            window.scrollTo(0, historicalLocation);
        } else {
            if (prevLocation?.pathname === currLocation.pathname) {
                let newLocationSearch: any = parseQueryParams(currLocation.search);
                let previousLocationSearch: any = parseQueryParams(prevLocation.search);

                /**
                 * If the previous route is the same as the current, but any of these
                 * four query params have changed, reset the scroll to top of page
                 */
                if (
                    newLocationSearch.page !== previousLocationSearch.page ||
                    newLocationSearch.sort !== previousLocationSearch.sort ||
                    newLocationSearch.tab !== previousLocationSearch.tab ||
                    newLocationSearch.folderId !== previousLocationSearch.folderId
                ) {
                    window.scrollTo(0, 0);
                }
            } else {
                window.scrollTo(0, 0);
            }
        }
        return onNewLocation;
    }, [currLocation, history, historyScrollLocations, prevLocation, routeIsPreloading]);

    return null;
};

export default ScrollMemory;
